<template>
  <DialogWrapper
    close-on-cancel
    destroyOnClose
    class="ai-comparison-history-dialog"
    hiddenHeaderLine
    hideFooter
    :loading="loading"
    :show.sync="isShow"
  >
    <TableList
      noAction
      class="history-list"
      :titleI18nKey="`${i18nPrefix}.label.aiComparisonHistoryTitle`"
      :listData="historyList"
      :tableColumns="tableColumns"
      :showCreateBtn="false"
      :pagination="pagination"
      @pagination-change="handlePaginationChange"
    >
      <template #createdTime="{ row }">
        <div class="date">{{ getCreatedDate(row.created_time) }}</div>
        <div class="time">{{ getCreatedTime(row.created_time) }}</div>
      </template>
      <template #candidateList="{ row }">
        <div class="candidate-avatar-list">
          <div
            v-for="(candidate, index) in row.candidate_list"
            class="candidate-avatar"
            :key="index"
          >
            <el-tooltip
              effect="light"
              popper-class="light-tooltip-popper"
              :content="candidate.name"
            >
              <div class="avatar-container">
                <img class="avatar" :style="{'--score-color': getComparisonScoreColor(row, candidate)}" :src="getCandidateAvatarPath(candidate)"/>
              </div>
            </el-tooltip>
          </div>
        </div>
      </template>
      <template #operation="{ row }">
        <el-button class="view-btn" @click="handleViewAITaskDetail(row)">{{ $t(`${i18nPrefix}.btn.viewAITaskDetail`) }}</el-button>
      </template>
    </TableList>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { getJobFitScoreColor } from '@/utils/common'

export default {
  components: {
    DialogWrapper,
    TableList
  },
  props: {
    jobId: {
      required: true
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      loading: false,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      historyList: [],
      noComparisonColor: '#fafafa'
    }
  },
  computed: {
    isShow: {
      get () {
        return this.show
      },
      set (val) {
        this.$emit('update:show', val)
      }
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.aiTaskCreatedTime`),
          width: '180',
          dataKey: 'created_time',
          slotName: 'createdTime'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.aiTaskCreatorName`),
          width: '200',
          dataKey: 'created_account.full_name',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.aiComparisonCandidates`),
          width: '240',
          dataKey: 'candidate_list',
          slotName: 'candidateList',
          showOverflowTooltip: false
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.aiTaskAction`),
          width: '140',
          dataKey: 'candidate_list',
          slotName: 'operation'
        }
      ]
      return columns
    }
  },
  created () {
  },
  methods: {
    ...mapActions('ai', ['getAIComparisonHistory']),
    initPage () {
      this.loadAIComparisonHistory()
    },
    async loadAIComparisonHistory () {
      this.loading = true
      try {
        const params = {
          job_id: this.jobId,
          page_num: this.pagination.page,
          page_size: this.pagination.pageSize
        }
        const resp = await this.getAIComparisonHistory(params)
        if (resp) {
          const scoreMap = {
            A: 5,
            B: 4,
            C: 3,
            D: 2,
            E: 1
          }
          _.forEach(resp.result, info => {
            const comparisonResult = info.comparison_result
            const hasResultCandidateIds = []
            const candidateList = _.chain(info.candidate_list).map(candidate => {
              const candidateComparisonInfo = _.find(comparisonResult, item => item.candidate_id === `${candidate.id}`)
              if (candidateComparisonInfo) {
                hasResultCandidateIds.push(candidate.id)
                candidate.totalScore = candidateComparisonInfo ? candidateComparisonInfo.total_score : null
                return candidate
              }
            }).filter().value()

            const list = this.formatListWithMountain(_.sortBy(candidateList, item => scoreMap[item.totalScore]))
            const noResultApplicationList = _.chain(info.candidate_list).filter(candidate => !_.includes(hasResultCandidateIds, candidate.id)).orderBy('id').value()
            _.forEach(noResultApplicationList, candidate => {
              list.push(candidate)
            })
            info.candidate_list = list
          })

          this.historyList = resp.result
          this.pagination.total = resp.total_count
        }
      } finally {
        this.loading = false
      }
    },
    /**
     * 将数组根据顺序成 '山型' 排列
     * @param list: 升序排列后的数组
     */
    formatListWithMountain (list) {
      // 从第一个开始隔两个取出一个
      const mountainLeft = []
      let mountainRight = []
      _.forEach(list, (item, index) => {
        if (index % 2 === 0) {
          mountainLeft.push(item)
        } else {
          mountainRight = _.concat(item, mountainRight)
        }
      })
      return _.concat(mountainLeft, mountainRight)
    },
    handlePaginationChange () {
      this.loadAIComparisonHistory()
    },
    getCreatedDate (time) {
      const i18nTime = moment(time).locale(this.$i18n.locale)
      const year = i18nTime.format('YYYY')
      const month = i18nTime.format('MMM')
      const weekday = i18nTime.format('dddd')
      const day = i18nTime.format('Do')
      return this.$t(`${this.i18nPrefix}.label.aiTaskTime`, { year, month, weekday, day })
    },
    getCreatedTime (time) {
      return moment(time).format('H:mm')
    },
    getCandidateAvatarPath (candidate) {
      return candidate.photo ? this.$DjangoBaseUrl(candidate.photo) : this.$DjangoStaticUrl('images_en/default.jpg')
    },
    getComparisonScoreColor (row, candidate) {
      const comparisonResult = row.comparison_result
      const candidateComparisonInfo = _.find(comparisonResult, item => item.candidate_id === `${candidate.id}`)
      if (!candidateComparisonInfo) {
        return this.noComparisonColor
      }
      const totalScore = candidateComparisonInfo.total_score
      return getJobFitScoreColor(totalScore)
    },
    handleViewAITaskDetail (row) {
      this.$emit('viewAIComparison', row.id)
    }
  },
  watch: {
    isShow () {
      if (this.isShow) {
        this.initPage()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-comparison-history-dialog {
  ::v-deep {
    .el-dialog {
      width: 64%;
    }
  }
  .history-list {
    .candidate-avatar-list {
      display: flex;
      align-items: center;
      overflow-x: auto;

      .candidate-avatar {
        display: flex;
        align-items: center;
        margin-right: 4px;
        .avatar-container {
          position: relative;
          flex-shrink: 0;

          .avatar {
            display: block;
            width: 44px;
            height: 44px;
            padding: 1px;
            border: 1px solid var(--score-color);
            border-radius: 50%;
            line-height: 1;
            cursor: pointer;
          }
        }
        &:last-child {
          margin-right: 0;
        }
      }
    }
    .view-btn {
      padding: 10px 26px;
      border-color: #0096cc;
      color: #0096cc;
    }
  }
}
/* 整个滚动条 */
::-webkit-scrollbar {
  height: 14px; /* 滚动条高度 */
  width: 14px; /* 滚动条宽度 */
}
</style>
